import React, { useEffect, useState, useContext, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { 
  listPlans, 
  createPlan, 
  getPlan, 
  updatePlan, 
  suggestSectionStream,
  actionPlan, 
  getGuideTips,
  getUsageLimits 
} from '../utils/apiServices';
import { getJSON, setJSON } from '../utils/errors';
import { SECTIONS, fieldMap, PREDEFINED_SECTIONS } from '../constants/plansConstants';
import { PlansSidebar, PlanEditor } from '../components/plans';
import '../stylesheets/plans.css';

const Plans = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [activeSlug, setActiveSlug] = useState(null);
  const [activePlan, setActivePlan] = useState(null);
  const [activeSection, setActiveSection] = useState(SECTIONS[0]);
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiError, setAiError] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [guide, setGuide] = useState(null);
  const [aiBySection, setAiBySection] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [usageLimits, setUsageLimits] = useState(null);

  // Cerrar modal guía con tecla Esc
  useEffect(() => {
    if (!guide) return;
    const handleKey = (e) => { if (e.key === 'Escape') setGuide(null); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [guide]);

  // Cerrar sidebar con tecla Esc
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape' && sidebarOpen) setSidebarOpen(false); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [sidebarOpen]);

  // Cargar lista de planes
  useEffect(() => {
    if (!user) return;
    listPlans().then(d => {
      const items = d.results || [];
      setPlans(items);
      const savedSlug = getJSON('last_plan_slug', null);
      const exists = items.find(p => p.slug === savedSlug);
      if (exists) {
        setActiveSlug(savedSlug);
      } else if (!activeSlug && items.length > 0) {
        setActiveSlug(items[0].slug);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Cargar plan activo y límites de uso
  useEffect(() => {
    if (!activeSlug) { 
      setActivePlan(null);
      setUsageLimits(null); 
      return; 
    }
    
    Promise.all([
      getPlan(activeSlug),
      getUsageLimits(activeSlug)
    ]).then(([p, limits]) => {
      setActivePlan(p);
      setUsageLimits(limits);
      const saved = getJSON(`last_ai_section_${p.slug}`, null);
      const sectionToRestore = (saved && SECTIONS.includes(saved)) ? saved : SECTIONS[0];
      setActiveSection(sectionToRestore);
      setAiError('');
      setGuide(null);
      setAi(null);

      // Pre-cargar caché en aiBySection sin abrir el drawer
      const key = `ai_suggestion_${p.slug}_${encodeURIComponent(sectionToRestore)}`;
      const parsed = getJSON(key, null);
      if (parsed) {
        setAiBySection({ [sectionToRestore]: parsed });
      } else {
        setAiBySection({});
      }
    });
  }, [activeSlug]);

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    const plan = await createPlan({ title });
    setPlans(prev => [plan, ...prev]);
    setActiveSlug(plan.slug);
    setJSON('last_plan_slug', plan.slug);
    setTitle('');
  };

  const handleSelectPlan = (slug) => {
    setActiveSlug(slug);
    setJSON('last_plan_slug', slug);
    setSidebarOpen(false);
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    setGuide(null);
    setAiError('');
    setAi(null);

    // Pre-cargar caché en aiBySection sin abrir el drawer
    if (!aiBySection[section] && activePlan) {
      const key = `ai_suggestion_${activePlan.slug}_${encodeURIComponent(section)}`;
      const parsed = getJSON(key, null);
      if (parsed) {
        setAiBySection(prev => ({ ...prev, [section]: parsed }));
      }
    }
  };

  const handleTextChange = (e) => {
    const key = fieldMap[activeSection];
    setActivePlan(prev => ({ ...prev, [key]: e.target.value }));
  };

  const handleSave = async () => {
    if (!activePlan) return;
    setSaving(true);
    const payload = Object.keys(fieldMap).reduce((acc, key) => {
      acc[fieldMap[key]] = activePlan[fieldMap[key]] || '';
      return acc;
    }, { title: activePlan.title });
    await updatePlan(activePlan.slug, payload);
    setSaving(false);
  };

  const streamingTextRef = useRef('');

  const handleSuggest = async () => {
    await handleSave();
    if (!activePlan) return;

    const currentSlug    = activePlan.slug;
    const currentSection = activeSection;
    const userText       = activePlan[fieldMap[currentSection]] || '';
    const isPredefined   = PREDEFINED_SECTIONS.has(currentSection);

    streamingTextRef.current = '';
    setAiError('');
    setAiLoading(true);
    setAi(isPredefined
      ? { streaming: false, ai_suggestion: '' }
      : { streaming: true, ai_suggestion: '' }
    );

    await suggestSectionStream(
      currentSlug,
      currentSection,
      userText,

      (chunk) => {
        streamingTextRef.current += chunk;
        setAi({
          streaming: !isPredefined,
          ai_suggestion: streamingTextRef.current,
        });
      },

      (fallbackData) => {
        const data = { ...fallbackData, streaming: false };
        setAi(data);
        setAiBySection(prev => ({ ...prev, [currentSection]: data }));
        const key = `ai_suggestion_${currentSlug}_${encodeURIComponent(currentSection)}`;
        setJSON(key, data);
        setJSON(`last_ai_section_${currentSlug}`, currentSection);
        setAiLoading(false);
      },

      () => {
        const finalData = {
          streaming: false,
          section: currentSection,
          ai_suggestion: streamingTextRef.current,
        };
        setAi(finalData);
        setAiBySection(prev => ({ ...prev, [currentSection]: finalData }));
        const key = `ai_suggestion_${currentSlug}_${encodeURIComponent(currentSection)}`;
        setJSON(key, finalData);
        setJSON(`last_ai_section_${currentSlug}`, currentSection);
        setAiLoading(false);
        if (!isPredefined) {
          getUsageLimits(currentSlug).then(limits => setUsageLimits(limits));
        }
      },

      (error) => {
        if (error?.response?.status === 429) {
          const errorData = error.response.data;
          setAiError(errorData.detail || 'Has alcanzado el límite de sugerencias para hoy.');
          getUsageLimits(currentSlug).then(limits => setUsageLimits(limits));
        } else {
          setAiError('No se pudo obtener sugerencias.');
        }
        setAi(null);
        setAiLoading(false);
      },
    );
  };

  // Verificar si todas las secciones están completas
  const areAllSectionsComplete = () => {
    if (!activePlan) return false;
    return SECTIONS.every(section => {
      const field = fieldMap[section];
      const value = activePlan[field];
      return value && value.trim().length > 0;
    });
  };

  // Obtener secciones incompletas
  const getIncompleteSections = () => {
    if (!activePlan) return [];
    return SECTIONS.filter(section => {
      const field = fieldMap[section];
      const value = activePlan[field];
      return !value || value.trim().length === 0;
    });
  };

  // Calcular progreso de un plan (% de secciones completas)
  const calculatePlanProgress = (plan) => {
    if (!plan) return 0;
    const completedCount = SECTIONS.filter(section => {
      const field = fieldMap[section];
      const value = plan[field];
      return value && value.trim().length > 0;
    }).length;
    return Math.round((completedCount / SECTIONS.length) * 100);
  };

  const handleActionPlan = async () => {
    if (!activePlan) return;

    // Verificar que todas las secciones estén completas
    if (!areAllSectionsComplete()) {
      const incomplete = getIncompleteSections();
      setAiError(
        `Debes completar todas las secciones antes de generar el plan de acción. Secciones faltantes: ${incomplete.join(', ')}.`
      );
      return;
    }

    try {
      setAiError('');
      setAiLoading(true);
      const data = await actionPlan(activePlan.slug);
      setActivePlan(prev => ({ 
        ...prev, 
        action_plan_md: data.actions_md, 
        action_plan_generated_at: data.generated_at 
      }));
      
      // Actualizar también la lista de planes para reflejar que ahora tiene plan de acción
      setPlans(prev => prev.map(p => 
        p.slug === activePlan.slug 
          ? { ...p, action_plan_md: data.actions_md, action_plan_generated_at: data.generated_at }
          : p
      ));
      
      // Actualizar límites después de generar
      getUsageLimits(activePlan.slug).then(limits => setUsageLimits(limits));
    } catch (e) {
      if (e.response?.data?.error === 'incomplete_sections') {
        setAiError(e.response.data.detail);
      } else if (e.response?.status === 429) {
        setAiError(e.response.data.detail || 'Has alcanzado el límite de planes de acción para hoy.');
        // Actualizar límites
        getUsageLimits(activePlan.slug).then(limits => setUsageLimits(limits));
      } else {
        setAiError('No se pudo generar el plan de acción.');
      }
    } finally {
      setAiLoading(false);
    }
  };

  const handleViewActionPlan = () => {
    if (activePlan) {
      navigate(`/plans/${activePlan.slug}/action-plan`);
    }
  };

  const handleShowGuide = async () => {
    const tips = await getGuideTips(activeSection);
    setGuide(tips);
  };

  const handleCloseGuide = () => {
    setGuide(null);
  };

  const handleCloseAi = () => {
    setAi(null);
    setAiError('');
  };

  if (!user) {
    return <Navigate to="/login/" replace />;
  }

  return (
    <>
      <div className="plans-container">
        <PlansSidebar
          plans={plans}
          activeSlug={activeSlug}
          title={title}
          setTitle={setTitle}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          onCreatePlan={handleCreatePlan}
          onSelectPlan={handleSelectPlan}
          calculateProgress={calculatePlanProgress}
        />

        <main className="editor-card">
          <PlanEditor
            activePlan={activePlan}
            activeSection={activeSection}
            fieldValue={activePlan ? activePlan[fieldMap[activeSection]] || '' : ''}
            saving={saving}
            aiLoading={aiLoading}
            ai={ai}
            aiError={aiError}
            guide={guide}
            allSectionsComplete={areAllSectionsComplete()}
            usageLimits={usageLimits}
            onSectionChange={handleSectionChange}
            onTextChange={handleTextChange}
            onSave={handleSave}
            onSuggest={handleSuggest}
            onGenerateActionPlan={handleActionPlan}
            onViewActionPlan={handleViewActionPlan}
            onShowGuide={handleShowGuide}
            onCloseGuide={handleCloseGuide}
            onCloseAi={handleCloseAi}
          />
        </main>
      </div>
    </>
  );
};

export default Plans;
